import { type DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fromDateTime, now } from '@/common/dateUtils'
import { getEventEndDate } from '@/common/utils'
import { type EventDetailsMode } from '@/features/eventDetails/types'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import { type EventData, type Store } from '@/types'

/**
 * Base store for event create/update/view sidebar. Contains basic values that can be used
 * in other stores.
 */
export const useEventDetailsBaseStore = defineStore('eventDetailsBaseStore', () => {
	const { setSelectedDate } = useCalendarStore()

	const startDate = ref<DateTime<true>>(now())
	const endDate = ref<DateTime<true>>(now())
	const store = ref<Store>()
	const name = ref('')
	const storeId = computed(() => store.value?.id)
	const sourceEvent = ref<EventData>()
	// If the action is update, there might be more events to be updated. They are stored in eventsRelatedToSourceEvent
	const eventsRelatedToSourceEvent = ref<EventData[]>()
	const mode = ref<EventDetailsMode>()

	/**
	 * Updates local start date and also sets given date as the "current" date in the timeline.
	 */
	function updateStartDate(date: DateTime) {
		if (!date.isValid || !storeId.value) {
			return
		}

		startDate.value = fromDateTime(date)

		setSelectedDate(storeId.value, date)
	}

	/**
	 * Updates local end date. If given date is before the start date then sets local end date
	 * to be the same as the start date.
	 */
	function updateEndDate(date: DateTime) {
		if (!date.isValid || !storeId.value) {
			return
		}

		endDate.value = fromDateTime(date)

		if (startDate.value.isValid && endDate.value.isValid && startDate.value > endDate.value) {
			// If end date points before start date then set it to be the same as start date
			endDate.value = fromDateTime(startDate.value as DateTime<true>)
		}
	}

	/**
	 * If local start date is valid, updates end date to that event length in days is given value.
	 */
	function updateEventLength(lengthInDays: number) {
		if (!startDate.value.isValid) {
			return
		}

		updateEndDate(getEventEndDate(startDate.value, lengthInDays))
	}

	function updateStore(value: Store | undefined) {
		store.value = value
	}

	function updateMode(value: EventDetailsMode | undefined) {
		mode.value = value
	}

	function updateSourceEvent(value: EventData | undefined) {
		sourceEvent.value = value
	}

	function updateRelatedEvents(value: EventData[] | undefined) {
		eventsRelatedToSourceEvent.value = value
	}

	function reset() {
		startDate.value = now()
		endDate.value = now()
		store.value = undefined
		sourceEvent.value = undefined
		eventsRelatedToSourceEvent.value = undefined
		name.value = ''
		mode.value = undefined
	}

	function doesSkuHaveEventRelatedToUpdatedEvent(skuId: string) {
		return eventsRelatedToSourceEvent.value?.find(({ unique_sku_id }) => skuId === unique_sku_id)
	}

	return {
		startDate: computed(() => startDate.value),
		endDate: computed(() => endDate.value),
		store: computed(() => store.value),
		sourceEvent: computed(() => sourceEvent.value),
		eventsRelatedToSourceEvent: computed(() => eventsRelatedToSourceEvent.value),
		mode: computed(() => mode.value),
		name,
		storeId,
		reset,
		updateStartDate,
		updateEndDate,
		updateEventLength,
		updateStore,
		updateSourceEvent,
		updateMode,
		updateRelatedEvents,
		doesSkuHaveEventRelatedToUpdatedEvent
	}
})
